/**
 * File: leads.ts
 * Path: packages/config/src/leads/leads.ts
 * Description: The leads pipeline shared by the admin and the server: stages,
 * the Google Maps search contract, and how a Maps business or a website
 * enquiry maps to a lead id.
 *
 * A business found on Google Maps is saved under an id derived from its Google
 * place id, so the same business can never be in the pipeline twice, whoever
 * saves it and from whichever device. That replaces the old scraper's
 * per-browser "already exported" list.
 */

import { resolveConsent, type Consent } from "../consent/consent";
import { toLeadAudit, type LeadAudit } from "./leadAudit";
import {
  toRecordBranches,
  toRecordContacts,
  toRecordGroup,
  type RecordBranch,
  type RecordContact,
  type RecordLink,
} from "./people";

/** Where a lead stands, in pipeline order. */
export const LEAD_STAGES = [
  "new",
  "contacted",
  "talking",
  "quoted",
  "won",
  "lost",
] as const;

export type LeadStage = (typeof LEAD_STAGES)[number];

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  new: "New",
  contacted: "Contacted",
  talking: "Talking",
  quoted: "Quote sent",
  won: "Won",
  lost: "Lost",
};

/** Stages a lead can still move on from. */
export const OPEN_LEAD_STAGES: readonly LeadStage[] = [
  "new",
  "contacted",
  "talking",
  "quoted",
];

export const isLeadStage = (value: unknown): value is LeadStage =>
  typeof value === "string" &&
  (LEAD_STAGES as readonly string[]).includes(value);

/**
 * Where a lead came from. "manual" is the old catch-all for a lead added by
 * hand: it still reads, shown as "Not sorted yet", and can be changed to a
 * real source on the lead.
 */
export type LeadSource =
  | "google_maps"
  | "cold_whatsapp"
  | "cold_email"
  | "referral"
  | "event"
  | "website"
  | "walk_in"
  | "manual";

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  google_maps: "Google Maps search",
  cold_whatsapp: "Cold WhatsApp",
  cold_email: "Cold email",
  referral: "Referral",
  event: "Event",
  website: "Website form",
  walk_in: "Walk-in or someone you know",
  manual: "Not sorted yet",
};

/** The sources someone can pick (the old "manual" is not one of them). */
export const LEAD_SOURCE_CHOICES: readonly LeadSource[] = [
  "google_maps",
  "cold_whatsapp",
  "cold_email",
  "referral",
  "event",
  "website",
  "walk_in",
];

/** Where a website enquiry came from: the ad or campaign tags on its link. */
export interface LeadCampaign {
  source: string;
  medium: string;
  campaign: string;
}

/** Where a lead came from, as it is picked and saved on the lead. */
export interface LeadOrigin {
  source: LeadSource;
  /** The referral partner, when the source is a referral. */
  affiliateId: string | null;
}

/** The stages whose first-reached time is kept on the lead as `<stage>At`. */
export const LEAD_STAGE_DATE_STAGES = ["contacted", "talking", "quoted", "won", "lost"] as const;
export type LeadStageDateStage = (typeof LEAD_STAGE_DATE_STAGES)[number];

/**
 * How many leads the pipeline loads, newest activity first. Higher than the
 * admin table page size on purpose: a board that silently hides the 51st open
 * lead loses a sale, and 500 small documents is one cheap read.
 */
export const LEADS_PIPELINE_LIMIT = 500;

/** Firestore document id for a business saved from Google Maps. */
export const leadIdForPlace = (placeId: string): string => `place_${placeId}`;

/**
 * A 64-bit hash as 16 hex characters: two independently mixed 32-bit lanes
 * (the cyrb53 construction, kept to the full 64 bits). Pure JS on purpose,
 * because this package also runs in the browser where Node's `crypto` is not
 * available, and the result only needs to be stable, not secret.
 */
const hash64 = (input: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return hex(h2) + hex(h1);
};

/**
 * Firestore document id for someone who wrote in through the website. Keyed
 * on the email so a person who writes twice stays one lead with two history
 * entries. Hashed rather than used raw: an email can't be a document id
 * as-is, and the id shouldn't spell out personal information in logs.
 */
export const leadIdForEmail = (email: string): string =>
  `web_${hash64(email.trim().toLowerCase())}`;

// ─── Google Maps search (callable `findMapLeads`) ──────────────────────────

/** Google returns at most 20 places per page and 3 pages per search. */
export const MAP_SEARCH_PAGE_SIZE = 20;
export const MAP_SEARCH_MAX_RESULTS = 60;
export const MAP_SEARCH_RESULT_OPTIONS = [20, 40, 60] as const;
export type MapSearchResultCount = (typeof MAP_SEARCH_RESULT_OPTIONS)[number];

export interface FindMapLeadsRequest {
  /** What the business does, e.g. "plumbers". */
  keyword: string;
  /** Where, e.g. "Grassy Park, Cape Town". */
  location: string;
  maxResults: MapSearchResultCount;
}

/** A business as Google Maps lists it, before it is saved as a lead. */
export interface MapLeadCandidate {
  placeId: string;
  name: string;
  phone: string;
  website: string;
  rating: number | null;
  reviewCount: number | null;
  address: string;
  /** Google's main category, e.g. "plumber". Empty when Google gives none. */
  primaryType: string;
  types: string[];
  /** OPERATIONAL, CLOSED_TEMPORARILY, CLOSED_PERMANENTLY, or empty. */
  businessStatus: string;
  googleMapsUri: string;
  /** True when this business is already in the pipeline. */
  alreadySaved: boolean;
}

export interface FindMapLeadsResponse {
  results: MapLeadCandidate[];
  /** Google had more results than were asked for. */
  hasMore: boolean;
  /** Google page requests left today for the whole team, after this search. */
  pagesLeftToday: number;
}

/** "plumber" → "Plumber", "car_repair" → "Car repair". */
export const formatPlaceType = (type: string): string => {
  const words = type.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

// ─── Stored leads ──────────────────────────────────────────────────────────

/** What Google Maps said about the business when it was saved. */
export interface LeadPlace {
  placeId: string;
  rating: number | null;
  reviewCount: number | null;
  primaryType: string;
  businessStatus: string;
  googleMapsUri: string;
}

/** A lead as the admin reads it: plain values, safe to render. */
export interface Lead {
  id: string;
  /** Business name. */
  name: string;
  /** The person to speak to, once known. */
  contactName: string;
  phone: string;
  email: string;
  /** http(s) only; anything else is dropped when read. */
  website: string;
  address: string;
  /** The area it was found in, e.g. "Grassy Park". */
  area: string;
  /** What the business does, e.g. "Plumber". */
  niche: string;
  source: LeadSource;
  /** The partner who sent them, when the source is a referral (affiliates/<id>). */
  affiliateId: string | null;
  /** The ad or campaign a website enquiry arrived through. */
  campaign: LeadCampaign | null;
  stage: LeadStage;
  lostReason: string;
  /** Estimated job value in rand, when known. */
  value: number | null;
  /** YYYY-MM-DD of the next planned follow-up. */
  nextFollowUp: string | null;
  /**
   * What the next step is (call, WhatsApp, email, meeting). A lead with a
   * date but no stored type reads as a call, the old follow-up default.
   */
  nextActionType: LeadContactType | null;
  /**
   * The next step is known but its date is not yet ("second meeting, they
   * couldn't confirm"). Such a lead stays quiet: no reminder, and not on the
   * "needs a next step" list, until a date is set. Only true when there is
   * no date.
   */
  nextStepTbd: boolean;
  /** POPIA: they asked not to be contacted. Never call or message them. */
  doNotContact: boolean;
  /** POPIA: they agreed to hear from us by email, SMS or WhatsApp. */
  marketingConsent: boolean;
  /** POPIA consent state with its date and how (consent/consent.ts); the two switches above win. */
  consent: Consent;
  /**
   * Set aside for later: kept, but off the board, out of the follow-up
   * counts and out of the morning email until someone brings it back.
   */
  parked: boolean;
  place: LeadPlace | null;
  /**
   * When the first WhatsApp message went out. POPIA allows one approach
   * without consent, so a second waits until they agree to hear from us.
   */
  openerSentAt: number | null;
  /** Follow-up calls made after the first message that went unanswered (followUps.ts). */
  followUpCalls: number;
  /** The last audit of their website, run from the lead's page. */
  audit: LeadAudit | null;
  /** When a report from an audit run elsewhere was last attached to the lead. */
  auditUploadedAt: number | null;
  /** Everyone else to speak to, beyond the main contact (people.ts). */
  contacts: RecordContact[];
  branches: RecordBranch[];
  /** Set when this lead is a branch split off from another record. */
  group: RecordLink | null;
  createdAt: number | null;
  updatedAt: number | null;
  stageChangedAt: number | null;
  /**
   * When the lead reached each stage. contactedAt, talkingAt and quotedAt
   * are the first time; wonAt and lostAt are set only while the lead is in
   * that stage (moving it on clears them), so a count of wonAt is a count of
   * deals that are won right now.
   */
  contactedAt: number | null;
  talkingAt: number | null;
  quotedAt: number | null;
  wonAt: number | null;
  lostAt: number | null;
}

/** The fields a person edits on a lead. */
export type LeadDetails = Pick<
  Lead,
  | "name"
  | "contactName"
  | "phone"
  | "email"
  | "website"
  | "address"
  | "area"
  | "niche"
  | "value"
  | "nextFollowUp"
  | "doNotContact"
  | "marketingConsent"
>;

/**
 * What a history entry is. Call, WhatsApp, email and meeting are logged
 * contact, the same four clients have, so outreach per day can be counted.
 */
export const LEAD_CONTACT_TYPES = ["call", "whatsapp", "email", "meeting"] as const;
export type LeadContactType = (typeof LEAD_CONTACT_TYPES)[number];

export const LEAD_CONTACT_LABELS: Record<LeadContactType, string> = {
  call: "Call",
  whatsapp: "WhatsApp",
  email: "Email",
  meeting: "Meeting",
};

export type LeadActivityType = "created" | "stage" | "note" | LeadContactType;

export interface LeadActivity {
  id: string;
  type: LeadActivityType;
  text: string;
  /** Stage changes only. */
  from: LeadStage | null;
  to: LeadStage | null;
  /** Email of whoever did it. */
  byEmail: string;
  at: number | null;
}

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const flag = (value: unknown): boolean => value === true;

const finiteOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Epoch millis from a number or a Firestore Timestamp (either SDK). */
const millis = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value && typeof value === "object") {
    const ts = value as { toMillis?: unknown; seconds?: unknown };
    if (typeof ts.toMillis === "function") return (ts.toMillis as () => number)();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
  }
  return null;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Business websites are often plain http, so both schemes are kept; anything
 * else (javascript:, data:) is dropped so a stored value can't become a script
 * link. A bare domain gets https:// added.
 */
export const safeWebUrl = (value: unknown): string => {
  const raw = text(value);
  if (!raw) return "";
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : "";
  } catch {
    return "";
  }
};

const LEAD_SOURCES: readonly LeadSource[] = [...LEAD_SOURCE_CHOICES, "manual"];

const toCampaign = (value: unknown): LeadCampaign | null => {
  if (!value || typeof value !== "object") return null;
  const c = value as Record<string, unknown>;
  const campaign = { source: text(c.source), medium: text(c.medium), campaign: text(c.campaign) };
  return campaign.source || campaign.medium || campaign.campaign ? campaign : null;
};

const toLeadPlace = (value: unknown): LeadPlace | null => {
  if (!value || typeof value !== "object") return null;
  const p = value as Record<string, unknown>;
  const placeId = text(p.placeId);
  if (!placeId) return null;
  return {
    placeId,
    rating: finiteOrNull(p.rating),
    reviewCount: finiteOrNull(p.reviewCount),
    primaryType: text(p.primaryType),
    businessStatus: text(p.businessStatus),
    googleMapsUri: safeWebUrl(p.googleMapsUri),
  };
};

/** Reads a stored lead, filling gaps so the admin never renders undefined. */
export const toLead = (id: string, data: Record<string, unknown>): Lead => {
  const source = LEAD_SOURCES.find((s) => s === data.source) ?? "manual";
  const value = finiteOrNull(data.value);
  const nextFollowUp = text(data.nextFollowUp);
  return {
    id,
    name: text(data.name) || "Unnamed business",
    contactName: text(data.contactName),
    phone: text(data.phone),
    email: text(data.email),
    website: safeWebUrl(data.website),
    address: text(data.address),
    area: text(data.area),
    niche: text(data.niche),
    source,
    affiliateId: text(data.affiliateId) || null,
    campaign: toCampaign(data.campaign),
    stage: isLeadStage(data.stage) ? data.stage : "new",
    lostReason: text(data.lostReason),
    value: value !== null && value >= 0 ? value : null,
    nextFollowUp: ISO_DATE.test(nextFollowUp) ? nextFollowUp : null,
    nextActionType: ISO_DATE.test(nextFollowUp)
      ? (LEAD_CONTACT_TYPES.find((t) => t === data.nextActionType) ?? "call")
      : flag(data.nextStepTbd)
        ? (LEAD_CONTACT_TYPES.find((t) => t === data.nextActionType) ?? null)
        : null,
    nextStepTbd: !ISO_DATE.test(nextFollowUp) && flag(data.nextStepTbd),
    doNotContact: flag(data.doNotContact),
    marketingConsent: flag(data.marketingConsent),
    consent: resolveConsent({
      stored: data.consent,
      doNotContact: flag(data.doNotContact),
      marketingConsent: flag(data.marketingConsent),
      firstMessageSent: millis(data.openerSentAt) !== null,
      isClient: false,
    }),
    parked: flag(data.parked),
    place: toLeadPlace(data.place),
    openerSentAt: millis(data.openerSentAt),
    followUpCalls:
      typeof data.followUpCalls === "number" && Number.isInteger(data.followUpCalls) && data.followUpCalls > 0
        ? data.followUpCalls
        : 0,
    audit: toLeadAudit(data.audit),
    auditUploadedAt: millis(data.auditUploadedAt),
    contacts: toRecordContacts(data.contacts),
    branches: toRecordBranches(data.branches),
    group: toRecordGroup(data.group),
    createdAt: millis(data.createdAt),
    updatedAt: millis(data.updatedAt),
    stageChangedAt: millis(data.stageChangedAt),
    contactedAt: millis(data.contactedAt),
    talkingAt: millis(data.talkingAt),
    quotedAt: millis(data.quotedAt),
    wonAt: millis(data.wonAt),
    lostAt: millis(data.lostAt),
  };
};

export const toLeadActivity = (
  id: string,
  data: Record<string, unknown>,
): LeadActivity => {
  const types: readonly LeadActivityType[] = ["created", "stage", "note", ...LEAD_CONTACT_TYPES];
  return {
    id,
    type: types.find((t) => t === data.type) ?? "note",
    text: text(data.text),
    from: isLeadStage(data.from) ? data.from : null,
    to: isLeadStage(data.to) ? data.to : null,
    byEmail: text(data.byEmail),
    at: millis(data.createdAt),
  };
};

/**
 * The stored shape of a business saved from a Maps search. `area` and `niche`
 * come from the search, so the pipeline can be filtered by them later.
 */
export const leadFromCandidate = (
  candidate: MapLeadCandidate,
  search: { keyword: string; location: string },
) => ({
  name: candidate.name || "Unnamed business",
  contactName: "",
  phone: candidate.phone,
  email: "",
  website: safeWebUrl(candidate.website),
  address: candidate.address,
  area: search.location.trim(),
  niche: formatPlaceType(candidate.primaryType) || search.keyword.trim(),
  source: "google_maps" as LeadSource,
  stage: "new" as LeadStage,
  lostReason: "",
  value: null,
  nextFollowUp: null,
  doNotContact: false,
  marketingConsent: false,
  place: {
    placeId: candidate.placeId,
    rating: candidate.rating,
    reviewCount: candidate.reviewCount,
    primaryType: candidate.primaryType,
    businessStatus: candidate.businessStatus,
    googleMapsUri: candidate.googleMapsUri,
  },
});

/** Longest business name the security rules accept on a lead. */
const LEAD_NAME_MAX = 200;

/**
 * The stored shape of someone who wrote in through the website form. They
 * are a person, not yet a business, so their name fills both name fields
 * until someone edits the lead. `marketingConsent` stays false: asking a
 * question is not agreeing to marketing (POPIA).
 */
export const leadFromEnquiry = (enquiry: {
  name: string;
  email: string;
  /** Ad or campaign tags the form was opened with (utm_source and friends). */
  campaign?: LeadCampaign | null;
}) => {
  const person = enquiry.name.trim().slice(0, LEAD_NAME_MAX);
  const email = enquiry.email.trim().toLowerCase();
  return {
    // The rules require a non-empty name; fall back to the email if the
    // submitted name was nothing but stripped markup.
    name: person || email.slice(0, LEAD_NAME_MAX) || "Website enquiry",
    contactName: person,
    phone: "",
    email,
    website: "",
    address: "",
    area: "",
    niche: "",
    source: "website" as LeadSource,
    stage: "new" as LeadStage,
    lostReason: "",
    value: null,
    nextFollowUp: null,
    doNotContact: false,
    marketingConsent: false,
    place: null,
    ...(enquiry.campaign && toCampaign(enquiry.campaign) ? { campaign: toCampaign(enquiry.campaign) } : {}),
  };
};

/** Today in South Africa as YYYY-MM-DD, for follow-up comparisons. */
export const todayInSouthAfrica = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(now);

/** An open lead whose follow-up date is today or earlier. */
export const isFollowUpDue = (lead: Lead, today: string = todayInSouthAfrica()): boolean =>
  !lead.parked &&
  lead.nextFollowUp !== null &&
  lead.nextFollowUp <= today &&
  OPEN_LEAD_STAGES.includes(lead.stage);

/**
 * Stages a staff quote moves on to "quoted". Later stages (quoted, won, lost)
 * stay where they are: a second quote, or a quote for an existing client,
 * must not drag a won deal back into the pipeline.
 */
export const QUOTABLE_LEAD_STAGES: readonly LeadStage[] = ["new", "contacted", "talking"];

/** Where a lead stands after staff send it a quote. */
export const stageAfterQuote = (stage: LeadStage): LeadStage =>
  QUOTABLE_LEAD_STAGES.includes(stage) ? "quoted" : stage;

/** The field readers above, shared with the clients and quotes readers. */
export { text as readText, flag as readFlag, finiteOrNull as readNumber, millis as readMillis };

/** When the lead was last audited: a run from the lead page or an attached report. */
export const lastAuditedAt = (lead: Pick<Lead, "audit" | "auditUploadedAt">): number | null => {
  const times = [lead.audit?.ranAt ?? null, lead.auditUploadedAt].filter((t): t is number => t !== null);
  return times.length ? Math.max(...times) : null;
};

/**
 * The one list of reasons a lead is lost, asked wherever a lead can be
 * lost. The reason is stored on the lead; an optional lesson goes in its
 * history.
 */
export const LOST_REASONS = [
  "Never replied",
  "Not interested",
  "Too expensive",
  "Went with someone else",
  "Bad timing",
  "Not a fit for us",
  "My approach was off",
] as const;

/** Kept for readers written before the name changed. */
export const TRASH_REASONS = LOST_REASONS;

/**
 * The stage-time fields to write when a lead moves to `to`: the first time
 * it reached contacted, talking or quoted (left alone once set), and wonAt /
 * lostAt only while it is in that stage (cleared on any other move). `now`
 * is the caller's timestamp (serverTimestamp() in the admin, a Timestamp on
 * the server).
 */
export const stageDateFields = <T>(
  lead: Pick<Lead, "contactedAt" | "talkingAt" | "quotedAt">,
  to: LeadStage,
  now: T,
): Record<string, T | null> => {
  const fields: Record<string, T | null> = {
    wonAt: to === "won" ? now : null,
    lostAt: to === "lost" ? now : null,
  };
  if (to === "contacted" && lead.contactedAt === null) fields.contactedAt = now;
  if (to === "talking" && lead.talkingAt === null) fields.talkingAt = now;
  if (to === "quoted" && lead.quotedAt === null) fields.quotedAt = now;
  return fields;
};
